/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography, CircularProgress, Stack } from '@mui/material';
import { LanguageContext } from '../../../../../../../../../libs/Context';
import { formatBytes, newNewsFeedUploadFiles } from '../../../../../../../../../libs/HelperContent';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useContentPermissions from '../../ContentCards/useContentPermissions';
import useUpdateStorage from '../../../../../../../../LicenseSubscription/customHooks/useGetStorage';

// Define accepted file types for different file categories
const acceptedImages = {
	'image/png'  : [ '.png' ],
	'image/gif'  : [ '.gif' ],
	'image/jpeg' : [ '.jpeg', '.jpg' ],
	'image/webp' : [ '.webp' ]
	// 'image/heif' : [ '.heic' ]

};

const acceptedVideos = {
	'video/mp4'       : [ '.mp4' ],
	'video/x-msvideo' : [ '.avi' ], // For .avi files
	'video/x-flv'     : [ '.flv' ], // For .flv files
	'video/webm'      : [ '.webm' ]
};

const acceptedDocs = {
	'application/msword'                                                        : [ '.doc' ], // For .doc files
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document'   : [ '.docx' ], // For .docx files
	'application/pdf'                                                           : [ '.pdf' ], // For .pdf files
	'application/vnd.ms-excel'                                                  : [ '.xls' ], // For .xls files
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'         : [ '.xlsx' ], // For .xlsx files
	'application/vnd.ms-powerpoint'                                             : [ '.ppt' ], // For .ppt files
	'application/vnd.openxmlformats-officedocument.presentationml.presentation' : [ '.pptx' ], // For .pptx files
	'text/plain'                                                                : [ '.txt' ] // For .txt files
};

// Add accepted audio files
const acceptedAudio = {
	'audio/mpeg'  : [ '.mp3' ],
	'audio/wav'   : [ '.wav' ],
	'audio/wma'   : [ '.wma' ],
	'audio/x-wav' : [ '.wav' ],
	'audio/x-m4a' : [ '.m4a' ]
};

const SystemUpload = ({ note, onFileSelect, onClose, fromCreation, fileType, setIsUploading, isUploading }) =>
{
	// const [ isUploading, setIsUploading ] = useState(false);
	const [ selectedFiles, setSelectedFiles ] = useState<File[]>([]);
	const userData = useSelector((state: any) => state.mysession.session);
	// Get upload permissions
	const { uploadSizeMBLimit } = useContentPermissions();
	const maxUploadFileSize = uploadSizeMBLimit * 1024 * 1024; // in bytes

	// Get storage update function
	const setStorageData = useUpdateStorage();

	// Get accepted file types based on fileType prop
	const getAcceptedFileTypes = () =>
	{
		if (!fileType) return { 'image/*': [] }; // Default to images

		switch (fileType.toLowerCase())
		{
			case 'video':
				return acceptedVideos;
			case 'document':
			case 'doc':
				return acceptedDocs;
			case 'audio':
				return acceptedAudio;
			case 'image':
			default:
				return acceptedImages;
		}
	};

	const processFile = async (file) =>
	{
		try
		{
			setIsUploading(true);

			// Check file size for videos and audio
			if ([ 'video', 'audio', 'image', 'document' ]?.includes(fromCreation) && file.size > maxUploadFileSize)
			{
				toast.error(`${fromCreation} file size exceeds the ${uploadSizeMBLimit}MB limit. File: ${file.name}`, {
					position  : toast.POSITION.TOP_RIGHT,
					className : 'toast-message'
				});
				setIsUploading(false);

				return false;
			}

			// Call storage API to add space before upload (for all uploads)
			try
			{
				await setStorageData({
					action         : 'add',
					size           : file.size,
					ownerCompanyId : userData?.companyId
				});
			}
			catch (storageError)
			{
				console.error('Storage API error:', storageError);
				toast.error('Storage allocation failed. Please try again.', {
					position  : toast.POSITION.TOP_RIGHT,
					className : 'toast-message'
				});
				setIsUploading(false);

				return false; // Storage API failed, don't proceed with upload
			}

			// throw new Error('test');

			if ([ 'image', 'video', 'audio', 'document' ]?.includes(fromCreation))
			{
				await onFileSelect(file, 'system', file.key);

				return true;
			}
			else
			{
				// Create path using company ID
				const path = `${userData?.companyId}/resource`;

				// Use the newNewsFeedUploadFiles function to upload
				const uploadResults = await newNewsFeedUploadFiles([ file ], path);

				if (uploadResults && uploadResults.length > 0)
				{
					const uploadedFile = uploadResults[0];
					// Pass the URL, source, and key to parent component

					onFileSelect(uploadedFile.url, 'system', uploadedFile.key);
					onClose();

					return true;
				}
				else
				{
					// If upload failed, remove the allocated storage
					await setStorageData({
						action         : 'remove',
						size           : file.size,
						ownerCompanyId : userData?.companyId
					});

					toast.error(`Failed to upload file. Please try again. ${file.name}`, {
						position  : toast.POSITION.TOP_RIGHT,
						className : 'toast-message'
					});

					return false;
				}
			}
		}
		catch (error: any)
		{
			console.error('Error uploading file:', error);

			// If there was an error, try to remove the allocated storage
			try
			{
				await setStorageData({
					action         : 'remove',
					size           : file.size,
					ownerCompanyId : userData?.companyId
				});
			}
			catch (storageError)
			{
				console.error('Error reverting storage allocation:', storageError);
			}

			toast.error(`Error uploading file: ${error.message || 'Unknown error'} ${file.name}`, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});

			return false;
		}
		finally
		{
			if (fromCreation !== 'video')
			{
				setIsUploading(false);
			}
		}
	};

	// Dropzone setup
	const onDrop = useCallback(async (acceptedFiles) =>
	{
		if (!acceptedFiles || acceptedFiles.length === 0) return;

		// Check if more than 5 files are selected for video/image/audio/document
		if ([ 'video', 'audio', 'image', 'document' ].includes(fromCreation) && acceptedFiles.length > 5)
		{
			toast.error('You can only select up to 5 files at a time', {
				position    : toast.POSITION.TOP_RIGHT,
				className   : 'toast-message',
				style       : { zIndex: 100000 },
				containerId : 'upload-dialog-toast'
			});

			return;
		}

		setSelectedFiles(acceptedFiles);

		// Check total file size for all accepted files
		const totalSize = acceptedFiles?.reduce((acc: number, file: File) => acc + file.size, 0);

		if (uploadSizeMBLimit && totalSize > maxUploadFileSize)
		{
			toast.error(`Total file size exceeds the limit ${uploadSizeMBLimit}MB`, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});
			setSelectedFiles([]);

			return;
		}

		if ([ 'video', 'image', 'audio', 'document' ]?.includes(fromCreation))
		{
			let successCount = 0;

			setIsUploading(true);

			for (let i = 0; i < acceptedFiles.length; i++)
			{
				const success = await processFile(acceptedFiles[i]);

				if (success) successCount++;
			}

			setIsUploading(false);
		}
		else
		{
			// For other non-video uploads, process only the first file
			await processFile(acceptedFiles[0]);
		}
	}, [ userData, fromCreation, uploadSizeMBLimit ]);

	const {
		getRootProps,
		getInputProps,
		isDragActive,
		isDragAccept,
		isDragReject,
		fileRejections
	} = useDropzone({
		onDrop,
		accept         : getAcceptedFileTypes(),
		maxFiles       : [ 'video', 'image', 'audio', 'document' ].includes(fromCreation) ? 5 : 1,
		multiple       : [ 'video', 'image', 'audio', 'document' ].includes(fromCreation),
		onDropRejected : (rejectedFiles) =>
		{
			if (rejectedFiles.some((file) => file.errors[0]?.code === 'too-many-files'))
			{
				toast.error('You can only select up to 5 files at a time', {
					position    : toast.POSITION.TOP_RIGHT,
					className   : 'toast-message',
					style       : { zIndex: 100000 },
					containerId : 'upload-dialog-toast'
				});
			}
		}
	});

	// Dynamic styles based on drag state
	const getDropzoneStyle = () =>
	{
		const style = {
			border         : '1px dashed #ccc',
			display        : 'flex',
			flexDirection  : 'column',
			alignItems     : 'center',
			justifyContent : 'center',
			padding        : 4,
			borderRadius   : 2,
			width          : '100%',
			transition     : 'border .2s ease-in-out'
		};

		if (isDragActive)
		{
			return {
				...style,
				border          : '2px dashed #24a4c4',
				backgroundColor : '#F7FCFD'
			};
		}

		if (isDragReject || fileRejections.length > 0)
		{
			return {
				...style,
				border          : '2px dashed #ff1744',
				backgroundColor : 'rgba(255, 23, 68, 0.1)'
			};
		}

		if (isDragAccept)
		{
			return {
				...style,
				border          : '2px dashed #2e7d32',
				backgroundColor : 'rgba(46, 125, 50, 0.1)'
			};
		}

		return style;
	};

	const maxFileSize = uploadSizeMBLimit;

	// Get helper text based on file type
	const getHelperText = () =>
	{
		if (!fileType) return 'Only image files are supported';

		switch (fileType.toLowerCase())
		{
			case 'video':
				return `Supported formats: MP4, AKV. ${fromCreation === 'video' ? `Maximum file size: ${uploadSizeMBLimit}MB. You can upload up to 5 files.` : ''}`;
			case 'document':
			case 'doc':
				return 'Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT';
			case 'audio':
				return `Supported formats: MP3, WAC. Maximum file size: ${uploadSizeMBLimit}MB.`;
			case 'image':
			default:
				return 'Supported formats: PNG, JPEG, GIF, WEBP';
		}
	};

	// Show selected files list
	const renderSelectedFiles = () =>
	{
		if (!selectedFiles.length) return null;

		return (
			<Box mt={2} width='100%'>
				<Typography fontSize={12} fontWeight={600} color={'#000'} mb={1}>
					Selected files:
				</Typography>
				{selectedFiles.map((file, index) => (
					<Stack
						key={index}
						direction='row'
						alignItems='center'
						spacing={1}
						sx={{
							fontSize        : '12px',
							padding         : '4px 8px',
							backgroundColor : '#f5f5f5',
							borderRadius    : '4px',
							marginBottom    : '4px'
						}}
					>
						<Typography fontSize={12} noWrap sx={{ flex: 1 }}>
							{file.name}
						</Typography>
						<Typography fontSize={10} color='text.secondary'>
							{formatBytes(file.size)}
						</Typography>
					</Stack>
				))}
			</Box>
		);
	};

	const bottomNote = useMemo(() => note.replace('{{file-size}}', formatBytes(maxUploadFileSize)), [ note, maxUploadFileSize ]);

	return (
		<Box
			sx={{
				display        : 'flex',
				flexDirection  : 'column',
				alignItems     : 'center',
				justifyContent : 'center',
				width          : '70%'
			}}
		>
			<Box
				{...getRootProps()}
				sx={getDropzoneStyle()}
			>
				{isUploading
					? (
						<>
							<CircularProgress size={50} />
							<Typography mt={2} fontSize={14} fontWeight={600} color={'#000'}>
								Uploading...
							</Typography>
						</>
					)
					: (
						<>
							<input {...getInputProps()} />
							<Box component={'img'} src='/images/Upload.svg'
								sx={{ height: '150px', width: '200px', objectFit: 'contain' }}
							/>
							<Typography fontSize={14} fontWeight={600} color={'#000'}>
								{isDragActive
									? 'Drop the file here'
									: isDragReject
										? `Upload supported ${fileType} only`
										: 'Drag & Drop here to upload files'
								}
							</Typography>
							<Typography fontSize={12} color={'#737373'} mt={1} mb={2} textAlign='center'>
								{isDragActive ? '' : getHelperText()}
							</Typography>
							<Button
								variant='contained'
								fullWidth
								sx={{
									color     : '#fff',
									marginTop : '10px',
									bgcolor   : '#24a4c4 !important'
								}}
							>
								Choose Files
							</Button>
						</>
					)}
			</Box>

			{renderSelectedFiles()}

			<Typography
				variant='caption'
				sx={{ marginTop: 2, color: '#949494' }}
				fontSize={14} fontWeight={600}
			>
				{bottomNote}
			</Typography>
		</Box>
	);
};

export default SystemUpload;
